import React, { useState, useEffect } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import TemplateCard from './TemplateCard';
import TemplatePagination from './TemplatePagination';
import './TemplateBrowser.css';

/**
 * Template Browser Component
 * Browse and search all available templates with filters
 */
const TemplateBrowser = ({ brandId, expertId, onExecute }) => {
    const [templates, setTemplates] = useState([]);
    const [templateTypes, setTemplateTypes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('all');
    const [pinnedTemplates, setPinnedTemplates] = useState([]);
    const [maxPins, setMaxPins] = useState(8);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        filtered: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false
    });

    // Debounce search term (300ms delay)
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    // Fetch template types on mount
    useEffect(() => {
        fetchTemplateTypes();
    }, [brandId, expertId]);

    // Fetch templates when filters change
    useEffect(() => {
        fetchTemplates(1); // Reset to page 1 on filter change
    }, [debouncedSearchTerm, selectedType, brandId, expertId]);

    // Fetch pinned templates
    useEffect(() => {
        fetchPinnedTemplates();
    }, [brandId, expertId]);

    const fetchTemplateTypes = async () => {
        try {
            const response = await fetch(`/api/chat/templates/types/${brandId}/${expertId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch template types');

            const data = await response.json();
            setTemplateTypes(data.types || []);

            console.log('[TemplateBrowser] ✅ Loaded template types:', data.types);
        } catch (err) {
            console.error('[TemplateBrowser] ❌ Error fetching template types:', err);
        }
    };

    const fetchTemplates = async (page = pagination.page) => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: page,
                limit: pagination.limit,
                ...(selectedType !== 'all' && { type: selectedType }),
                ...(debouncedSearchTerm && { search: debouncedSearchTerm })
            });

            console.log('[TemplateBrowser] 🔍 Fetching templates:', {
                page,
                type: selectedType,
                search: debouncedSearchTerm
            });

            const response = await fetch(
                `/api/chat/templates/${brandId}/${expertId}?${params}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (!response.ok) throw new Error('Failed to fetch templates');

            const data = await response.json();
            setTemplates(data.templates || []);
            setPagination(data.pagination);

            console.log('[TemplateBrowser] ✅ Loaded templates:', {
                count: data.templates?.length,
                total: data.pagination?.total,
                filtered: data.pagination?.filtered
            });

        } catch (err) {
            console.error('[TemplateBrowser] ❌ Error fetching templates:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchPinnedTemplates = async () => {
        try {
            const response = await fetch(
                `/api/chat/templates/pinned/${brandId}/${expertId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (!response.ok) throw new Error('Failed to fetch pinned templates');

            const data = await response.json();
            setPinnedTemplates(data.pinned || []);
            setMaxPins(data.max || 8);

            console.log('[TemplateBrowser] 📌 Loaded pinned templates:', data.pinned?.length);
        } catch (err) {
            console.error('[TemplateBrowser] ❌ Error fetching pinned templates:', err);
        }
    };

    const handlePin = async (templateId) => {
        console.log('[TemplateBrowser] 📌 Pinning template:', templateId);

        try {
            const response = await fetch('/api/chat/templates/pin', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ templateId, brandId, expertId })
            });

            const data = await response.json();

            if (!response.ok) {
                alert(data.error || 'Failed to pin template');
                return;
            }

            console.log('[TemplateBrowser] ✅ Pin successful');

            // Refresh both lists
            fetchTemplates();
            fetchPinnedTemplates();

        } catch (err) {
            console.error('[TemplateBrowser] ❌ Pin failed:', err);
            alert('Failed to pin template');
        }
    };

    const handlePageChange = (newPage) => {
        fetchTemplates(newPage);
        // Scroll to top of template list
        document.querySelector('.template-list')?.scrollTo(0, 0);
    };

    return (
        <div className="template-browser">
            <div className="browser-header">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="🔍 Search by ID or name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                    {searchTerm !== debouncedSearchTerm && (
                        <span className="search-debouncing">Searching...</span>
                    )}
                </div>

                <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="type-filter"
                >
                    <option value="all">All Types</option>
                    {templateTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                    ))}
                </select>
            </div>

            {error && (
                <div className="error-message">
                    ❌ {error}
                </div>
            )}

            <div className="template-list">
                {loading ? (
                    <div className="loading-state">
                        <p>Loading templates...</p>
                    </div>
                ) : templates.length === 0 ? (
                    <div className="empty-state">
                        <p>No templates found</p>
                        {(searchTerm || selectedType !== 'all') && (
                            <p className="empty-hint">Try adjusting your search or filter</p>
                        )}
                    </div>
                ) : (
                    templates.map(template => (
                        <TemplateCard
                            key={template.id}
                            template={template}
                            onPin={handlePin}
                            onExecute={onExecute}
                            isPinned={template.is_pinned}
                            maxPinsReached={pinnedTemplates.length >= maxPins}
                        />
                    ))
                )}
            </div>

            {/* Results info and pagination on the right side - positioned absolutely */}
            <div className="results-footer">
                <div className="results-info">
                    {loading ? (
                        <span>Loading...</span>
                    ) : (
                        <>
                            Showing {templates.length} of {pagination.filtered} templates
                            {pagination.filtered < pagination.total && (
                                <span className="filter-note">
                                    {' '}(filtered from {pagination.total} total)
                                </span>
                            )}
                        </>
                    )}
                </div>

                {pagination.totalPages > 1 && (
                    <TemplatePagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    );
};

export default TemplateBrowser;
